import dayjs from 'dayjs';
import { RefObject, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { ContentPlaceholder } from '../../../../../components/content-placeholder/content-placeholder';
import { skyLight } from '../../../../../styles/design-tokens';
import { LineChartDot } from '../../components/line-chart/line-chart-dot';
import { FocusChartLegend } from './focus-chart-legend';
import { FocusChartTooltip } from './focus-chart-tooltip';
import { getSelectionNames, hasFocusData } from './focus-chart.helpers';
import { useChartData, useFocusData, useSelection, useSelectionColors } from './focus-chart.hooks';

type Props = {
  chartRef: RefObject<HTMLDivElement> | null;
};

export const FocusChart = ({ chartRef }: Props) => {
  const i18n = useTranslation();
  const focusData = useFocusData();
  const selection = useSelection();

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const chartData = useChartData();
  const selectionColors = useSelectionColors(selection);
  const selectionNames = getSelectionNames(selection);

  if (!hasFocusData(focusData)) {
    return <ContentPlaceholder />;
  }

  if (selection.length === 0) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  return (
    <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={160}>
        <LineChart data={chartData} margin={{ top: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            padding={{ right: 10 }}
            tickFormatter={(value) => dayjs(value).format('MMM, YYYY')}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            axisLine={{ stroke: skyLight }}
            tickLine={{ stroke: skyLight }}
            label={{
              value: i18n.t('strategy.initiative_performance.chart_labels.focus'),
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle' },
              offset: 10,
            }}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<FocusChartTooltip selectionNames={selectionNames} selectionColors={selectionColors} />} />
          {selection.map((item) => {
            const itemId = item.id;
            const isHovered = hoveredItem === null || hoveredItem === itemId;

            return (
              <Line
                key={itemId}
                type="monotone"
                dataKey={itemId}
                name={item.name}
                stroke={selectionColors[itemId]}
                strokeWidth={isHovered ? 2 : 0.7}
                opacity={isHovered ? 1 : 0.3}
                dot={<LineChartDot color={selectionColors[itemId]} />}
                activeDot={<LineChartDot color={selectionColors[itemId]} />}
                isAnimationActive={false}
              />
            );
          })}
          <Legend
            content={
              <FocusChartLegend
                selectionNames={selectionNames}
                selectionColors={selectionColors}
                hoveredItem={hoveredItem}
                setHoveredItem={setHoveredItem}
              />
            }
            verticalAlign="bottom"
            height={36}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
